import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wallet, Search, Phone, MapPin, ExternalLink, Check, Circle, User } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useOrders } from '../hooks/useOrders'
import { KANBAN_COLUNAS } from '../data/kanban'
import { formatCurrency, formatTelefone } from '../utils/fmt'
import { saveBlingLogisticaItem } from '../utils/storage'

// Estilos por status (mesma paleta da Logística)
const STATUS_STYLE = {
  CADASTRADO: { bg: '#94A3B8', text: '#fff' },
  FABRICANDO: { bg: '#FBBF24', text: '#111' },
  EXPEDICAO:  { bg: '#4ADE80', text: '#111' },
  ROTA:       { bg: '#FB923C', text: '#fff' },
  ENTREGUE:   { bg: '#F9A8D4', text: '#9D174D' },
  AJUSTE:     { bg: '#93C5FD', text: '#1E40AF' },
  POS_VENDA:  { bg: '#F9A8D4', text: '#9D174D' },
}

const FORMAS_LABEL = { pix: 'Pix', dinheiro: 'Dinheiro', credito: 'Crédito', debito: 'Débito' }

function fmtDate(d) {
  if (!d || d === '0000-00-00') return '—'
  try { return format(new Date(d + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR }) } catch { return d }
}

// Mês do prazo em "YYYY-MM" → label "Junho/2026"
function mesPorPrazo(prazo) {
  if (!prazo) return null
  return prazo.slice(0, 7)
}

function fmtMesLabel(yyyymm) {
  if (!yyyymm) return ''
  try {
    return format(new Date(yyyymm + '-15T12:00:00'), "MMMM/yyyy", { locale: ptBR })
  } catch { return yyyymm }
}

export function ContasAReceber() {
  const navigate = useNavigate()
  const { pedidos, salvar, refresh } = useOrders()
  const [busca, setBusca]             = useState('')
  const [statusSel, setStatusSel]     = useState('TODOS')
  const [mesSel, setMesSel]           = useState('TODOS')
  const [pagoSel, setPagoSel]         = useState('PENDENTE')   // PENDENTE | PAGO | TODOS
  const [vendedoraSel, setVendedoraSel] = useState('TODOS')    // TODOS | <nome> | SEM

  // Base: pedidos com valor pendente > 0 (mostra inclusive já entregues)
  const aReceber = useMemo(
    () => pedidos.filter(p => Number(p.valorPendenteEntrega || 0) > 0),
    [pedidos]
  )

  // Marca/desmarca como pago — persiste em pedido.pagoEntrega (locais)
  // ou via override em saveBlingLogisticaItem (Bling).
  const togglePago = useCallback((p) => {
    const novo = !p.pagoEntrega
    if (String(p.id).startsWith('bling-')) {
      saveBlingLogisticaItem(p.id, { pagoEntrega: novo })
      refresh()
    } else {
      salvar({ ...p, pagoEntrega: novo })
    }
  }, [salvar, refresh])

  // Meses únicos disponíveis (do prazo)
  const mesesDisponiveis = useMemo(() => {
    const set = new Set()
    aReceber.forEach(p => {
      const m = mesPorPrazo(p.prazo)
      if (m) set.add(m)
    })
    return [...set].sort()
  }, [aReceber])

  // Vendedoras únicas disponíveis (ordenadas, ignorando vazios)
  const vendedorasDisponiveis = useMemo(() => {
    const set = new Set()
    aReceber.forEach(p => {
      const v = (p.vendedora || '').trim()
      if (v) set.add(v)
    })
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [aReceber])

  // Aplicação de filtros
  const filtrados = useMemo(() => {
    let l = aReceber
    if (pagoSel === 'PENDENTE') l = l.filter(p => !p.pagoEntrega)
    if (pagoSel === 'PAGO')     l = l.filter(p =>  p.pagoEntrega)
    if (statusSel !== 'TODOS')  l = l.filter(p => p.status === statusSel)
    if (mesSel !== 'TODOS')     l = l.filter(p => mesPorPrazo(p.prazo) === mesSel)
    if (vendedoraSel === 'SEM')      l = l.filter(p => !(p.vendedora || '').trim())
    else if (vendedoraSel !== 'TODOS') l = l.filter(p => (p.vendedora || '').trim() === vendedoraSel)
    if (busca.trim()) {
      const t = busca.toLowerCase().trim()
      const tDig = t.replace(/\D/g, '')
      l = l.filter(p => {
        const nome     = (p.cliente?.nome     || '').toLowerCase()
        const tel      = (p.cliente?.contato  || '').replace(/\D/g, '')
        const numero   = String(p.numero || '').toLowerCase()
        return nome.includes(t) ||
               numero.includes(t) ||
               (tDig && tel.includes(tDig))
      })
    }
    return l.sort((a, b) => (a.prazo || '~') < (b.prazo || '~') ? -1 : 1)
  }, [aReceber, busca, statusSel, mesSel, pagoSel, vendedoraSel])

  // Contagens pra chips
  const countsByStatus = useMemo(() => {
    const map = { TODOS: aReceber.length }
    KANBAN_COLUNAS.forEach(col => {
      map[col.id] = aReceber.filter(p => p.status === col.id).length
    })
    return map
  }, [aReceber])

  const countsByMes = useMemo(() => {
    const map = { TODOS: aReceber.length }
    mesesDisponiveis.forEach(m => {
      map[m] = aReceber.filter(p => mesPorPrazo(p.prazo) === m).length
    })
    return map
  }, [aReceber, mesesDisponiveis])

  // Totais divididos: pendente e recebido
  const totalPendente = useMemo(
    () => aReceber.filter(p => !p.pagoEntrega).reduce((s, p) => s + Number(p.valorPendenteEntrega || 0), 0),
    [aReceber]
  )
  const totalRecebido = useMemo(
    () => aReceber.filter(p =>  p.pagoEntrega).reduce((s, p) => s + Number(p.valorPendenteEntrega || 0), 0),
    [aReceber]
  )
  const totalFiltrado  = useMemo(() => filtrados.reduce((s, p) => s + Number(p.valorPendenteEntrega || 0), 0), [filtrados])
  const filtroAtivo    = busca.trim() || statusSel !== 'TODOS' || mesSel !== 'TODOS' || pagoSel !== 'PENDENTE' || vendedoraSel !== 'TODOS'

  // Contagem pra chips de pagamento
  const countsPagamento = useMemo(() => ({
    PENDENTE: aReceber.filter(p => !p.pagoEntrega).length,
    PAGO:     aReceber.filter(p =>  p.pagoEntrega).length,
    TODOS:    aReceber.length,
  }), [aReceber])

  // Contagem por vendedora (apenas pedidos PENDENTES — é o que importa pra cobrança).
  // Inclui chave "SEM" pra pedidos sem vendedora preenchida e "TODOS" pro total.
  const countsByVendedora = useMemo(() => {
    const pendentes = aReceber.filter(p => !p.pagoEntrega)
    const map = { TODOS: pendentes.length, SEM: 0 }
    pendentes.forEach(p => {
      const v = (p.vendedora || '').trim()
      if (v) map[v] = (map[v] || 0) + 1
      else map.SEM++
    })
    return map
  }, [aReceber])

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Wallet size={20} className="text-[#F5C200]" />
            Contas a Receber
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Pedidos com saldo pendente — clique no círculo pra marcar como pago
          </p>
        </div>
        <div className="flex items-end gap-4">
          <div className="text-right">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">A receber</p>
            <p className="text-2xl font-bold text-[#CC8800]">{formatCurrency(totalPendente)}</p>
          </div>
          <div className="text-right opacity-80">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Já recebido</p>
            <p className="text-base font-semibold text-emerald-600">{formatCurrency(totalRecebido)}</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card p-3 mb-4 space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text" value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar por cliente, telefone ou número do pedido…"
            className="input text-sm pl-9 w-full"
          />
        </div>

        {/* Chips de pagamento */}
        <div className="flex flex-wrap gap-1">
          {[
            { id: 'PENDENTE', label: 'Pendentes', cls: 'bg-amber-100 text-amber-800' },
            { id: 'PAGO',     label: 'Pagos',     cls: 'bg-emerald-100 text-emerald-800' },
            { id: 'TODOS',    label: 'Todos',     cls: 'bg-[#111111] text-[#F5C200]' },
          ].map(o => (
            <button
              key={o.id}
              onClick={() => setPagoSel(o.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${pagoSel === o.id ? o.cls : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            >
              {o.label} ({countsPagamento[o.id]})
            </button>
          ))}
        </div>

        {/* Chips de vendedora — contagem reflete só pendentes, que é o foco da cobrança */}
        {vendedorasDisponiveis.length > 0 && (
          <div className="flex flex-wrap gap-1 items-center">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-400 mr-1">
              <User size={11} />
              Vendedora
            </span>
            <button
              onClick={() => setVendedoraSel('TODOS')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${vendedoraSel === 'TODOS' ? 'bg-[#111111] text-[#F5C200]' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            >
              Todas ({countsByVendedora.TODOS})
            </button>
            {vendedorasDisponiveis.map(v => {
              const c = countsByVendedora[v] || 0
              const ativo = vendedoraSel === v
              return (
                <button
                  key={v}
                  onClick={() => setVendedoraSel(v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                    ${ativo ? 'bg-[#F5C200]/20 text-[#CC8800] ring-1 ring-[#F5C200]' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                >
                  {v} ({c})
                </button>
              )
            })}
            {countsByVendedora.SEM > 0 && (
              <button
                onClick={() => setVendedoraSel('SEM')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                  ${vendedoraSel === 'SEM' ? 'bg-slate-300 text-slate-800' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                Sem vendedora ({countsByVendedora.SEM})
              </button>
            )}
          </div>
        )}

        {/* Chips de status */}
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setStatusSel('TODOS')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
              ${statusSel === 'TODOS' ? 'bg-[#111111] text-[#F5C200]' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          >
            Todos os status ({countsByStatus.TODOS})
          </button>
          {KANBAN_COLUNAS.map(col => {
            const c = countsByStatus[col.id] || 0
            if (c === 0) return null
            const ativo = statusSel === col.id
            const s = STATUS_STYLE[col.id] || STATUS_STYLE.CADASTRADO
            return (
              <button
                key={col.id}
                onClick={() => setStatusSel(col.id)}
                style={ativo ? { backgroundColor: s.bg, color: s.text } : {}}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                  ${ativo ? '' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                {col.label} ({c})
              </button>
            )
          })}
        </div>

        {/* Chips de mês */}
        {mesesDisponiveis.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setMesSel('TODOS')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${mesSel === 'TODOS' ? 'bg-[#F5C200]/20 text-[#CC8800]' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            >
              Todos os meses
            </button>
            {mesesDisponiveis.map(m => {
              const ativo = mesSel === m
              return (
                <button
                  key={m}
                  onClick={() => setMesSel(m)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors capitalize
                    ${ativo ? 'bg-[#F5C200]/20 text-[#CC8800] ring-1 ring-[#F5C200]' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                >
                  {fmtMesLabel(m)} ({countsByMes[m] || 0})
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Lista */}
      {filtrados.length === 0 ? (
        <div className="card p-10 text-center">
          <Wallet className="mx-auto text-slate-200 mb-3" size={32} />
          <p className="text-sm text-slate-400">
            {aReceber.length === 0
              ? 'Nenhum pedido com saldo pendente'
              : 'Nenhum pedido corresponde aos filtros'
            }
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Cabeçalho da tabela */}
          <div className="hidden md:flex items-center bg-[#111111] text-[#F5C200] text-[10px] font-bold uppercase tracking-wide">
            <div className="w-[44px] shrink-0 px-2 py-2.5 text-center">Pago</div>
            <div className="w-[140px] shrink-0 px-3 py-2.5 border-l border-white/10">Status</div>
            <div className="flex-1 px-3 py-2.5 border-l border-white/10">Cliente</div>
            <div className="w-[140px] shrink-0 px-3 py-2.5 border-l border-white/10">Telefone</div>
            <div className="w-[110px] shrink-0 px-3 py-2.5 border-l border-white/10">Prazo</div>
            <div className="w-[100px] shrink-0 px-3 py-2.5 border-l border-white/10 text-right">Total</div>
            <div className="w-[120px] shrink-0 px-3 py-2.5 border-l border-white/10 text-right">A receber</div>
            <div className="w-[40px] shrink-0 border-l border-white/10" />
          </div>

          {/* Linhas */}
          {filtrados.map((p, i) => {
            const s    = STATUS_STYLE[p.status] || STATUS_STYLE.CADASTRADO
            const col  = KANBAN_COLUNAS.find(c => c.id === p.status)
            const tel  = formatTelefone(p.cliente?.contato)
            const pago = !!p.pagoEntrega
            return (
              <div
                key={p.id}
                onClick={() => navigate(`/pedidos/${p.id}`)}
                className={`flex flex-col md:flex-row md:items-center text-sm border-b border-slate-50 last:border-b-0
                  cursor-pointer transition-colors
                  ${pago
                    ? 'bg-emerald-50/60 hover:bg-emerald-50'
                    : `hover:bg-[#FFFDF5] ${i % 2 === 1 ? 'bg-slate-50/40' : ''}`}`}
              >
                {/* Checkpoint Pago */}
                <div className="w-full md:w-[44px] shrink-0 px-2 py-2 flex md:justify-center items-center">
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); togglePago(p) }}
                    title={pago ? 'Marcado como pago — clique pra desmarcar' : 'Marcar como pago'}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                      ${pago
                        ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
                        : 'bg-white border-slate-300 text-transparent hover:border-emerald-400 hover:text-emerald-400'}`}
                  >
                    <Check size={14} strokeWidth={3} />
                  </button>
                </div>

                {/* Status */}
                <div className="w-full md:w-[140px] shrink-0 px-3 py-2 md:border-l border-slate-100 flex items-center">
                  <span
                    style={{ backgroundColor: s.bg, color: s.text }}
                    className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide"
                  >
                    {col?.label || p.status}
                  </span>
                </div>

                {/* Cliente + nº + vendedora */}
                <div className="flex-1 px-3 py-2 md:border-l border-slate-100">
                  <p className={`font-semibold leading-snug ${pago ? 'text-slate-500 line-through' : 'text-slate-800'}`}>
                    {p.cliente?.nome || '—'}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-2">
                    <span className="font-mono">{p.numero}</span>
                    {p.vendedora && (
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        <User size={9} />
                        {p.vendedora}
                      </span>
                    )}
                  </p>
                </div>

                {/* Telefone */}
                <div className="w-full md:w-[140px] shrink-0 px-3 py-2 md:border-l border-slate-100">
                  {tel ? (
                    <a
                      href={`https://wa.me/55${(p.cliente?.contato || '').replace(/\D/g, '').replace(/^55/, '')}`}
                      target="_blank" rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="text-xs text-[#CC8800] hover:underline flex items-center gap-1"
                    >
                      <Phone size={10} />
                      {tel}
                    </a>
                  ) : <span className="text-xs text-slate-300">—</span>}
                </div>

                {/* Prazo */}
                <div className="w-full md:w-[110px] shrink-0 px-3 py-2 md:border-l border-slate-100">
                  <span className="text-xs text-slate-600">{fmtDate(p.prazo)}</span>
                </div>

                {/* Total do pedido */}
                <div className="w-full md:w-[100px] shrink-0 px-3 py-2 md:border-l border-slate-100 md:text-right">
                  <span className="text-xs text-slate-500">{formatCurrency(p.valorTotal)}</span>
                </div>

                {/* Pendente — destaque (verde tachado quando pago) */}
                <div className="w-full md:w-[120px] shrink-0 px-3 py-2 md:border-l border-slate-100 md:text-right">
                  <span className={`text-sm font-bold ${pago ? 'text-emerald-600 line-through' : 'text-[#CC8800]'}`}>
                    {formatCurrency(p.valorPendenteEntrega)}
                  </span>
                  {p.formaPagamento && (
                    <p className="text-[9px] text-slate-400 uppercase mt-0.5">
                      {FORMAS_LABEL[p.formaPagamento] || p.formaPagamento}
                    </p>
                  )}
                </div>

                {/* Ir pro detalhe */}
                <div className="hidden md:flex w-[40px] shrink-0 items-center justify-center md:border-l border-slate-100">
                  <ExternalLink size={13} className="text-slate-300" />
                </div>
              </div>
            )
          })}

          {/* Rodapé com total filtrado */}
          {filtroAtivo && (
            <div className="flex items-center justify-end gap-3 px-4 py-3 bg-slate-50 border-t border-slate-100">
              <span className="text-xs text-slate-500">Total filtrado:</span>
              <span className="text-base font-bold text-[#CC8800]">
                {formatCurrency(totalFiltrado)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
